import { useContext, useState } from "react";

import { Button } from "@aidron/aidron-ds";

import {
  LoadingContent,
  FilterButton,
  TableDownloadButton,
  SuccessModal,
  CancelModalButton,
} from "../../../components/SharedComponents/sharedComponents.index";
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { Default } from "../../../components/Default/Default";

import { Sidebar } from "../../../components/SharedComponents/Sidebar/Sidebar";
import { TenantContext } from "../../../templates/context/TenantContext";

import { SalesRecommendationTable } from "./salesRecommendation.index";
import { SupplierConfirmationModal } from "./components/SuppliersConfirmationModal/SuppliersConfirmationModal"
import { HierarchyFiltersSalesRecommendation } from "./components/HierarchyFiltersSalesRecommendation/HierarchyFiltersSalesRecomendation";

import { i18n } from "../../../translate/i18n";
import { usePurchaseRecommendationViewModel } from "../../../viewModel/usePurchaseManagementViewModel";
import { useStoreInformation } from "../../../viewModel/stores/storeInformationStore";
import { useSeetingsViewModel } from "../../../viewModel/useSettingsViewModel";

import { HeaderWithFilters, HEADER_STRATEGY_CONSTANTS } from "../../../viewModel/strategy/headers/HeaderWithFilters/headerWithFilters.strategy";
import { ButtonDisplayStrategy } from "../../../viewModel/strategy/buttons/buttons.strategy";
import { CustomHeading, CustomText } from "../../../components/StyledComponents/styleComponents.index";

import { buildModalConfig, renderModal } from "../../../viewModel/builder";
import { useAnalysisViewModel } from "../../../viewModel/useAnalysysViewModel";

export function SalesRecomendation () {

  const [sharedAppliedLevels, setSharedAppliedLevels] = useState({});
  const [sharedSelectedLevels, setSharedSelectedLevels] = useState({});
  const [sharedTypedExpression, setSharedTypedExpression] = useState("");
  const [sharedAppliedExpression, setSharedAppliedExpression] = useState("");  

  const { selectedStoreNumber, setStore, selectedStoreId } = useStoreInformation();
  const tenantContext = useContext(TenantContext) || {};

  const vm = usePurchaseRecommendationViewModel({
    shouldFetchPurchaseRecommendationData: true,
    shouldFetchOrdersData: false
  });
  const vmSettings = useSeetingsViewModel({ shouldFetchUserCtx: true, shouldFetchUsersInformation: true });
  const vmAnalysis = useAnalysisViewModel({
    shouldFetchProducts: !!selectedStoreId,
    storeId: selectedStoreId
  })

  const hierarchyNames = vmSettings?.ctx?.hierarchyNames;

  const { t } = i18n;

  const modals = buildModalConfig({
    mode: "salesRecommendation",
    actions: {
      ...vm?.actions,
      setSharedAppliedLevels: setSharedAppliedLevels,
      setSharedSelectedLevels: setSharedSelectedLevels,
      setSharedTypedExpression: setSharedTypedExpression,
      setSharedAppliedExpression: setSharedAppliedExpression
    },
    ctx: {
      ...vm?.ctx,
      t: t,
      hierarchyNames: hierarchyNames,
      products: vmAnalysis?.ctx?.products
    },
    components: {
      SupplierConfirmationModal,
      SuccessModal,
      CancelModalButton,
    },
    uiState: {
      ...vm?.uiState,
      sharedAppliedExpression: sharedAppliedExpression,
      sharedAppliedLevels: sharedAppliedLevels,
      sharedSelectedLevels: sharedSelectedLevels,
      sharedTypedExpression: sharedTypedExpression
    }
  })

  if (vm.uiState.isLoading) return <LoadingContent label={t("loadingStoresRec")} type={LOADING_CONTENT_TYPE.API_PAGE_DATA}/>
  if (vm.uiState.isLoadingStores) return <LoadingContent label={t("loadingStores")} type={LOADING_CONTENT_TYPE.API_PAGE_DATA} />;

  return(
    <Default>
      <div style={{ display: "flex", minHeight: "100vh" }}>
        {/* <Sidebar ctx={{ userRole: vmSettings?.ctx?.userRole, tenantContext }} /> */}

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", inlineSize: "100%", padding: "2rem 0 0 1rem"}}>

          {/* Opção de seletores */}
          <HeaderWithFilters
            actions={{ setSelectedSecondaryFilterValues: setStore, setShowInputs: vm.actions.setShowInputs }}
            ctx={{
              selectedSecondaryFilterValues: selectedStoreNumber || [],
              allSecondaryOptions: vm.ctx.allStoresNames || [],
              allPrimaryOptions: vm.ctx.allRegionsNames || [],
              numberToDisplay: vm?.uiState?.purchaseOrderEligibility?.eligibleOrders,
              labels: {
                titlePage: t("openPurchaseOrders"),
                secondSelect: t("store"),
                filterAction: t("selectAction")
              }
            }}
            uiState={{ 
              isLoadingStores: vm.uiState.isLoadingStores, 
              showInputs: vm.uiState.showInputs,
              showCard: true,
              userRole: vmSettings.ctx.userRole,
              headerStyle: {
                useProvidedHeaderStyle: true,
                providedHeaderStyle: {
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 3fr",
                  inlineSize: "100%",
                  placeItems: "center"
                }
              }
            }}
            mode={HEADER_STRATEGY_CONSTANTS.DEFAULT}
          />

          {/* Toggle para exibição de produtos elegíveis (compra final != 0) e não elegíveis (compra final === 0) */}
          <section style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
            <CustomText fontSize={['0.3rem', '0.6rem', '1rem']} weight="bold">
              Também exibir produtos com{" "}
              <span style={{ color: "red" }}>Compra Final</span>{" "}
              zerados
            </CustomText>

            <label className="switch">
              <input
                type="checkbox"
                checked={vm?.uiState?.showNonEligibleProducts}
                onChange={(e) =>
                  vm?.actions?.handleToggleEligibleProducts(e.target.checked)
                }
              />
              <span className="slider" />
            </label>
          </section>

          {/* Opções da tabela (botões de filtro) */}
          <section style={{
            alignItems: 'center',
            backgroundColor: "#fff",
            border: "0.0625rem solid rgba(17, 24, 39, 0.125)",
            borderRadius: '0.25rem',
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.04)",
            display: 'flex',
            flexDirection: 'row',
            gap: '1rem',
            justifyContent: 'space-between',
            padding: "9px 16px"}}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              {/* Botões de filtro selecionados, exibição de filtros e nome da página */}
              <CustomHeading fontSize={['0.5rem', '0.9rem', '1.2rem']} margin="0" fontWeight={500} >
                {t("storePurchaseOrders")}
              </CustomHeading>            

              <FilterButton
                actions={{
                  setShowFilters: (value) => {
                    vm?.actions?.setShowFilters(value);
                    if (value) vm?.actions?.setShowColumnFilters(false);
                  }
                }}
                uiState={{
                  showFilters: vm?.uiState?.showFilters,
                  appliedFiltersCount: vm?.uiState?.appliedFiltersCount,
                  size: "sm",
                  disabled: !vm?.ctx?.supplierProductRelations || vm?.uiState?.showColumnFilters
                }}/>              

              <Button
                onClick={(value) => {
                  vm?.actions?.setShowColumnFilters(value);
                  if (value) vm.actions?.setShowFilters(false);
                }}
                label=""
                icon="layout"
                type={vm?.uiState?.showColumnFilters ? "filled" : "outlined"}
                size="sm"
                disabled={vm?.uiState?.showFilters || !vm?.ctx?.supplierProductRelations}/>    
            </div>

          {/* Botões para ações e renderizações de modais */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem' }}>

            <ButtonDisplayStrategy 
              mode={"purchase_recommendation_table"}
              actions={{
                setShowInputs: vm?.actions?.setShowInputs,
                setShowCancelModal: vm?.actions?.setShowCancelModal,
                onInputsSave: vm?.actions?.onInputsSave,
                setOpenOrderConfirmation: vm?.actions?.setOpenOrderConfirmation,
                setShowUpdateRecommendationError: vm?.actions?.setShowUpdateRecommendationError
              }}
              ctx={{ suppliersAndProductRelations: vm?.ctx?.supplierProductRelations }}
              uiState={{
                showInputs: vm?.uiState?.showInputs,
                showUpdateRecommendationError: vm?.uiState?.showUpdateRecommendationError,
                saveLoading: vm?.uiState?.isSaving,
                isPendingPurchaseOrders: true,
                customDisabledCondition: false,
                allowOrderSubmission: vm?.uiState?.purchaseOrderEligibility?.withUserInputs,
                errorOfPurchaseRecommendationUpdate: vm?.uiState?.errorOfPurchaseRecommendationUpdate                
              }}
            />

            {renderModal(modals)}

          </div>

          {/* Botão de download */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem' }}>
            <TableDownloadButton 
              ctx={{
                tableInfo: vm?.ctx?.tableInfo,
                sheetName: "Forecast",
                defaultFileName: "loja"          
              }}
              uiState={{
                isStoresTable: true,
                disabled: !vm?.ctx?.supplierProductRelations     
              }}/>
              
          </div>        
          </section>

          {/* Filtro via fluxo de processo de compra 1 */}
          <HierarchyFiltersSalesRecommendation
            actions={{
              setAppliedFiltersCount: vm?.actions?.setAppliedFiltersCount,
              setSelectedProductIds: vm?.actions?.setSelectedProductIds,
              setShowFilters: vm?.actions?.setShowFilters,
              setSelectedSupplierFromFilter: vm?.actions?.setSelectedSupplierFromFilter,
              setAppliedLevels: setSharedAppliedLevels,
              setSelectedLevels: setSharedSelectedLevels,
              setTypedExpression: setSharedTypedExpression,
              setAppliedExpression: setSharedAppliedExpression
            }}
            ctx={{ 
              productIds: vm?.ctx?.productIds,
              suppliersAndProductRelations: vm?.ctx?.supplierProductRelations, 
              hierarchyNames: hierarchyNames,
              products: vmAnalysis?.ctx?.products
            }}
            uiState={{ 
              showFilters: vm?.uiState?.showFilters,
              appliedLevels: sharedAppliedLevels, 
              selectedLevels: sharedSelectedLevels,
              typedExpression: sharedTypedExpression,
              appliedExpression: sharedAppliedExpression
            }}
          />

          {/* Tabela de produtos */}
          <SalesRecommendationTable
            actions={{
              setFreezeColumns: vm.actions.setFreezeColumns,
              setShowColumnFilters: vm.actions.setShowColumnFilters,
              setSorting: vm.actions.setSorting,
            }}
            ctx={{
              purchaseRecommendationData: vm.ctx.filteredData,
              columns: vm.ctx.columnsSalesTable,
            }}
            uiState={{
              showInputs: vm.uiState.showInputs,
              freezeColumns: vm.uiState.freezeColumns,
              showColumnFilters: vm.uiState.showColumnFilters,
              saveLoading: vm.uiState.isUpdating,
              inputs: vm.uiState.inputs,
              sorting: vm.uiState.sorting
            }}/>                  
        </div>
      </div>      
    </Default>
  )
};